type SupportedStorage = Pick<Storage, 'getItem' | 'setItem' | 'removeItem'>

/** JWT típico ~500 chars. Tokens > 16KB causam ERR_HTTP2_PROTOCOL_ERROR (header too long). */
const MAX_ACCESS_TOKEN_CHARS = 16 * 1024

const isAuthTokenKey = (key: string): boolean =>
  key.startsWith('sb-') && key.endsWith('-auth-token')

const parseSessionAccessToken = (value: string | null): string | null => {
  if (!value || typeof value !== 'string') return null
  try {
    const parsed = JSON.parse(value) as { access_token?: string }
    return typeof parsed?.access_token === 'string' ? parsed.access_token : null
  } catch {
    return null
  }
}

/** Rejeita sessões com token gigante (ex.: app_metadata inflado) que causa ERR_HTTP2_PROTOCOL_ERROR. */
const isSessionTokenOversized = (value: string | null, key: string): boolean => {
  if (!isAuthTokenKey(key)) return false
  const token = parseSessionAccessToken(value)
  return token !== null && token.length > MAX_ACCESS_TOKEN_CHARS
}

const isQuotaExceededError = (error: unknown): boolean => {
  if (!error) return false
  if (!(error instanceof Error)) return false

  const anyError = error as unknown as { name?: unknown; code?: unknown }
  const name = typeof anyError.name === 'string' ? anyError.name : ''
  const code = typeof anyError.code === 'number' ? anyError.code : undefined

  // Common across Chromium/WebKit/Firefox.
  return (
    name === 'QuotaExceededError' ||
    name === 'NS_ERROR_DOM_QUOTA_REACHED' ||
    code === 22 ||
    code === 1014
  )
}

let evictAuthTokensRun = false

const evictForAuthTokenWrite = (currentKey: string) => {
  if (typeof window === 'undefined') return

  try {
    for (let i = window.localStorage.length - 1; i >= 0; i -= 1) {
      const key = window.localStorage.key(i)
      if (!key) continue
      if (key === currentKey) continue

      // Old zustand/local caches can become huge and are safe to evict.
      // Preserve synap-session-store so the app can hydrate user before getMe completes.
      if (key.startsWith('synap-') && key !== 'synap-session-store') {
        window.localStorage.removeItem(key)
        continue
      }

      // Preview environments can leave many auth tokens around; keep only the active key.
      if (key.startsWith('sb-') && key.endsWith('-auth-token')) {
        window.localStorage.removeItem(key)
        continue
      }
    }
  } catch {
    // ignore
  }
}

/** Limpa tokens antigos na primeira leitura (evita acúmulo em previews). */
const maybeEvictStaleAuthTokens = (currentKey: string) => {
  if (!evictAuthTokensRun && isAuthTokenKey(currentKey) && typeof window !== 'undefined') {
    evictAuthTokensRun = true
    evictForAuthTokenWrite(currentKey)
  }
}

export const createSafeAuthStorage = (): SupportedStorage => {
  const memory = new Map<string, string>()

  const primary = typeof window !== 'undefined' ? window.localStorage : undefined
  const secondary = typeof window !== 'undefined' ? window.sessionStorage : undefined

  const safeGet = (storage: Storage | undefined, key: string): string | null => {
    if (!storage) return null
    try {
      return storage.getItem(key)
    } catch {
      return null
    }
  }

  const safeRemove = (storage: Storage | undefined, key: string) => {
    if (!storage) return
    try {
      storage.removeItem(key)
    } catch {
      // ignore
    }
  }

  return {
    getItem: (key: string) => {
      maybeEvictStaleAuthTokens(key)
      let value = safeGet(primary, key) ?? safeGet(secondary, key)
      value = value ?? memory.get(key) ?? null

      if (value !== null && isSessionTokenOversized(value, key)) {
        safeRemove(primary, key)
        safeRemove(secondary, key)
        memory.delete(key)
        return null
      }
      return value
    },

    setItem: (key: string, value: string) => {
      if (isAuthTokenKey(key) && isSessionTokenOversized(value, key)) {
        safeRemove(primary, key)
        safeRemove(secondary, key)
        return
      }

      if (!primary) {
        memory.set(key, value)
        return
      }

      try {
        primary.setItem(key, value)
        return
      } catch (error) {
        if (isQuotaExceededError(error)) {
          evictForAuthTokenWrite(key)
          try {
            primary.setItem(key, value)
            return
          } catch {
            // fall through
          }
        }
      }

      if (secondary) {
        try {
          secondary.setItem(key, value)
          return
        } catch {
          // fall through
        }
      }

      // Last resort: keep auth session in-memory (prevents login from failing).
      memory.set(key, value)
    },

    removeItem: (key: string) => {
      safeRemove(primary, key)
      safeRemove(secondary, key)
      memory.delete(key)
    },
  }
}

